import { Icon } from "../Icon";
import { Switch } from "../Switch";
import { forwardRef } from "react";
import {
  Box,
  BoxProps,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { COLOR_CONSTANTS, TYPOGRAPHY_SCALE } from "../../constants/styles";
import useLocale from "src/domains/shared/hooks/localeHook/useLocale";
import { useTranslation } from "react-i18next";

/**
 * 지도 하단 이미지 패널 props — 화면 근처/전체 이미지 카운트, 즐겨찾기 토글, 접힘 토글.
 * `children` 슬롯으로 `ImageCard` 목록을 가로 스크롤 형태로 끼운다.
 */
export interface MapBottomProps extends BoxProps {
  /** 필터링된 이미지 개수 */
  nearbyImageCount: number;
  /** 전체 이미지 개수 */
  totalImageCount: number;
  /**
   * 개수를 사실로 단정해도 되는가. 조회가 성공으로 종결됐을 때만 true 다 —
   * 미종결·실패 구간의 "0/0" 은 "이미지가 0장" 이라는 거짓 단정이 되고,
   * 이 헤더는 패널이 접힌 기본 상태에서도 항상 보이므로 오류 갈래보다 먼저 눈에 띈다.
   * 생략하면 기존 동작(항상 표시)을 유지한다.
   */
  isCountKnown?: boolean;
  /** 즐겨찾기만 보기 스위치 활성화 상태 */
  favorite: boolean;
  /** 즐겨찾기 스위치 변경 핸들러 */
  onFavoriteClick?: () => void;
  /** 이미지 리스트 접힘 상태 (true: 접힘, false: 펼침) */
  collapsed: boolean;
  /** 접힘/펼침 토글 핸들러 */
  onToggleCollapse?: () => void;
}

export const MapBottom = forwardRef<HTMLDivElement, MapBottomProps>(
  (
    {
      children,
      nearbyImageCount = 0,
      totalImageCount = 0,
      isCountKnown = true,
      favorite = false,
      onFavoriteClick,
      collapsed = false,
      onToggleCollapse,
      ...props
    },
    ref
  ) => {
        const { t } = useTranslation("timeSeries");
    const [locale] = useLocale();
    const currentLocale = locale.locale as "ko" | "en";

    return (
      <Box ref={ref} sx={container} {...props}>
        <Box sx={header}>
          <Box sx={leftContent}>
            <Typography sx={TYPOGRAPHY_SCALE.h6}>
              {t("map.imageList", { ns: "timeSeries" })}
            </Typography>
            <Box sx={imageCountContainer}>
              <Typography sx={imageCount}>
                {isCountKnown ? `${nearbyImageCount}/${totalImageCount}` : "-"}
              </Typography>
              {!favorite && (
                <Box sx={nearbyDescriptionContainer}>
                  <Tooltip
                    title={t("map.nearbyImagesOnly", { ns: "timeSeries" })}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      size="small"
                      sx={questionIconButton}
                      disableRipple
                      disableFocusRipple
                    >
                      <Icon
                        name="etc/QuestionCircle"
                        size={16}
                        color="#ffffff"
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={favoriteButton}>
              <Typography sx={TYPOGRAPHY_SCALE.subtitle2}>
                {t("map.favoriteOnly", { ns: "timeSeries" })}
              </Typography>
              <Switch
                size="small"
                color="primary"
                checked={favorite}
                onChange={onFavoriteClick}
              />
            </Box>
          </Box>
          <IconButton onClick={onToggleCollapse}>
            <Icon
              name={collapsed ? "etc/Unfold" : "etc/Fold"}
              size={24}
              clickable
            />
          </IconButton>
        </Box>
        {!collapsed && (
          <Box
            sx={{
              ...imageList,
            }}
          >
            {children}
          </Box>
        )}
      </Box>
    );
  }
);

MapBottom.displayName = "MapBottom";

const container = {
  width: "100%",
  overflow: "hidden",
  color: COLOR_CONSTANTS.primary.contrastText,
  backgroundColor: COLOR_CONSTANTS.COLOR.GREY[800],
} as const;

const header = {
  width: "100%",
  height: 37,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "rgba(0, 0, 0, 0.2)",
} as const;

const leftContent = {
  padding: "8px 16px",
  display: "flex",
  alignItems: "center",
  gap: "16px",
} as const;

const imageCount = {
  ...TYPOGRAPHY_SCALE.subtitle2,
  textAlign: "center",
} as const;

const imageCountContainer = {
  display: "flex",
  alignItems: "center",
} as const;

const nearbyDescriptionContainer = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
} as const;

const questionIconButton = {
  padding: "4px",
  color: COLOR_CONSTANTS.text.disabled,
  "&:hover": {
    backgroundColor: "transparent",
    color: COLOR_CONSTANTS.text.secondary,
  },
  "&:focus": {
    backgroundColor: "transparent",
  },
} as const;

const favoriteButton = {
  display: "flex",
  alignItems: "center",
  gap: 1,
} as const;

const imageList = {
  width: "100%",
  height: "145px",
  padding: "8px 16px",
  display: "flex",
  alignItems: "center",
  flexWrap: "nowrap",
  gap: 2,
  overflowX: "scroll",
  overflowY: "hidden",
  boxSizing: "border-box",
  "& > *": {
    flexShrink: 0,
  },
  "&::-webkit-scrollbar": {
    height: "15px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#353535",
  },
  "&::-webkit-scrollbar-thumb": {
    padding: "0",
    borderRadius: "8px",
    backgroundColor: "black",
    border: "5px solid #353535",
  },
} as const;
